import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import styles from './RoundTabs.module.scss';

const MAX_TAB_LABEL = 19;

const RoundTabs = ({ rounds, activeRoundId, onRoundChange }) => {
  const containerRef = useRef(null);

  // Keep the active tab visible when the round changes from outside
  // (e.g. user scrolls the bracket and BracketCanvas reports the new round).
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !activeRoundId) return;

    const activeTab = container.querySelector(`[data-round-id="${activeRoundId}"]`);
    if (!activeTab) return;

    // Center the active tab horizontally inside the scrollable container.
    const containerRect = container.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const delta = (tabRect.left + tabRect.width / 2)
                - (containerRect.left + containerRect.width / 2);

    if (Math.abs(delta) > 1) {
      container.scrollTo({
        left: container.scrollLeft + delta,
        behavior: 'smooth',
      });
    }
  }, [activeRoundId]);

  const handleClick = (roundId) => {
    if (onRoundChange && roundId !== activeRoundId) {
      onRoundChange(roundId);
    }
  };

  return (
    <div
      ref={containerRef}
      className={styles['round-tabs']}
      role="tablist"
      aria-label="Rondas del torneo"
    >
      {rounds.map((round) => {
        const isActive = round.id === activeRoundId;

        return (
          <button
            key={round.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-round-id={round.id}
            className={classNames(styles['round-tabs__tab'], {
              [styles['round-tabs__tab--active']]: isActive,
            })}
            onClick={() => handleClick(round.id)}
          >
            {renderMaxLengthText(round.label || '', MAX_TAB_LABEL)}
          </button>
        );
      })}
    </div>
  );
};

RoundTabs.propTypes = {
  rounds: PropTypes.arrayOf(
    PropTypes.shape({
      id:    PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  activeRoundId: PropTypes.string,
  onRoundChange: PropTypes.func,
};

RoundTabs.defaultProps = {
  activeRoundId: null,
  onRoundChange: null,
};

export default RoundTabs;
